/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Color } from 'three';
import { ViewportTransitionActor } from './ViewportTransitionActor';
/**
 * represents a viewport of the stage
 */
export class Viewport {
    /**
     * constructor
     * @param name name of the viewport
     * @param scene name of the scene to show
     * @param camera name of the camera to use
     * @param bgcolor background color
     */
    constructor(viewports, name, scene, camera, bgcolor) {
        this._changed = false;
        this._viewports = viewports;
        this.name = name;
        this.scene = scene;
        this.camera = camera;
        this.bgcolor = bgcolor;
    }
    static fromDescription(viewports, vp, optionalName) {
        const res = new Viewport(viewports, vp.name || optionalName, vp.scene || 'default', vp.camera || 'default', new Color(vp.bgcolor));
        res.left = vp.left;
        res.top = vp.top;
        res.right = vp.right;
        res.bottom = vp.bottom;
        res.width = vp.width;
        res.height = vp.height;
        res.leftAbs = vp.leftAbs;
        res.topAbs = vp.topAbs;
        res.rightAbs = vp.rightAbs;
        res.bottomAbs = vp.bottomAbs;
        res.widthAbs = vp.widthAbs;
        res.heightAbs = vp.heightAbs;
        res.alpha = vp.alpha;
        res.noclear = vp.noclear;
        res.navigator = vp.navigator || 'default';
        res.parent = vp.parent;
        return res;
    }
    isIn(x, y, screenW, screenH) {
        const l = this.getLeft(screenW);
        const t = this.getTop(screenH);
        const w = this.getWidth(screenW);
        const h = this.getHeight(screenH);
        return l <= x && (l + w) > x && t <= y && (t + h) > y;
    }
    getLeft(screenWidth) {
        return this._getHorizontal(screenWidth)[0];
    }
    getRight(screenWidth) {
        return this._getHorizontal(screenWidth)[2];
    }
    getTop(screenHeight) {
        return this._getVertical(screenHeight)[0];
    }
    getBottom(screenHeight) {
        return this._getVertical(screenHeight)[2];
    }
    getWidth(screenWidth) {
        return this._getHorizontal(screenWidth)[1];
    }
    getHeight(screenHeight) {
        return this._getVertical(screenHeight)[1];
    }
    doTransition(stage, targetViewport, duration) {
        if (duration > 0) {
            const transition = new ViewportTransitionActor(this);
            transition.setDuration(duration);
            transition.addTarget('left', 'number', this.left, targetViewport.left);
            transition.addTarget('right', 'number', this.right, targetViewport.right);
            transition.addTarget('top', 'number', this.top, targetViewport.top);
            transition.addTarget('bottom', 'number', this.bottom, targetViewport.bottom);
            transition.addTarget('width', 'number', this.width, targetViewport.width);
            transition.addTarget('height', 'number', this.height, targetViewport.height);
            transition.addTarget('leftAbs', 'number', this.leftAbs, targetViewport.leftAbs);
            transition.addTarget('rightAbs', 'number', this.rightAbs, targetViewport.rightAbs);
            transition.addTarget('topAbs', 'number', this.topAbs, targetViewport.topAbs);
            transition.addTarget('bottomAbs', 'number', this.bottomAbs, targetViewport.bottomAbs);
            transition.addTarget('widthAbs', 'number', this.widthAbs, targetViewport.widthAbs);
            transition.addTarget('heightAbs', 'number', this.heightAbs, targetViewport.heightAbs);
            transition.addTarget('bgcolor', 'Color', this.bgcolor, new Color(targetViewport.bgcolor));
            stage.addActor(transition);
        }
        else {
            this.left = targetViewport.left;
            this.right = targetViewport.right;
            this.top = targetViewport.top;
            this.bottom = targetViewport.bottom;
            this.width = targetViewport.width;
            this.height = targetViewport.height;
            this.leftAbs = targetViewport.leftAbs;
            this.rightAbs = targetViewport.rightAbs;
            this.topAbs = targetViewport.topAbs;
            this.bottomAbs = targetViewport.bottomAbs;
            this.widthAbs = targetViewport.widthAbs;
            this.heightAbs = targetViewport.heightAbs;
            this.bgcolor = new Color(targetViewport.bgcolor);
            this.alpha = targetViewport.alpha;
        }
    }
    /**
     * checks if something changes since the last call of this function!!!
     */
    hasChanged() {
        const out = this._changed;
        this._changed = false;
        return out;
    }
    _getVertical(s) {
        let sh = s;
        let st = 0;
        let sb = 0;
        if (this.parent) {
            const parent = this._viewports.getByName(this.parent);
            [st, sh, sb] = parent._getVertical(s);
        }
        const ranges = this._add([st, 0, sb], this._getRanges(sh, this._t(sh), this._h(sh), this._b(sh)));
        const ne = !this._compareRanges(this._lastVertical, ranges);
        this._changed = this._changed || ne;
        if (ne) {
            this._lastVertical = ranges;
        }
        return ranges;
    }
    _getHorizontal(s) {
        let sw = s;
        let sl = 0;
        let sr = 0;
        if (this.parent) {
            const parent = this._viewports.getByName(this.parent);
            [sl, sw, sr] = parent._getHorizontal(s);
        }
        const ranges = this._add([sl, 0, sr], this._getRanges(sw, this._l(sw), this._w(sw), this._r(sw)));
        const ne = !this._compareRanges(this._lastHorizontal, ranges);
        this._changed = this._changed || ne;
        if (ne) {
            this._lastHorizontal = ranges;
        }
        return ranges;
    }
    _compareRanges(a, b) {
        const res = a && b
            && a[0] === b[0]
            && a[1] === b[1]
            && a[2] === b[2];
        return res;
    }
    _add(a, b) {
        a[0] += b[0];
        a[1] += b[1];
        a[2] += b[2];
        return a;
    }
    _checkRanges(...ranges) {
        return (ranges[0] !== undefined ? 4 : 0) + (ranges[1] !== undefined ? 2 : 0) + (ranges[2] !== undefined ? 1 : 0);
    }
    // tslint:disable:function-name
    _l(s) {
        return this.left !== undefined ? (this.left * s) : this.leftAbs;
    }
    _r(s) {
        return this.right !== undefined ? (this.right * s) : this.rightAbs;
    }
    _t(s) {
        return this.top !== undefined ? (this.top * s) : this.topAbs;
    }
    _b(s) {
        return this.bottom !== undefined ? (this.bottom * s) : this.bottomAbs;
    }
    _w(s) {
        return this.width !== undefined ? (this.width * s) : this.widthAbs;
    }
    _h(s) {
        return this.height !== undefined ? (this.height * s) : this.heightAbs;
    }
    /**
     *  in absolut ranges lwr / thb
     */
    _getRanges(s, ...ranges) {
        const combi = this._checkRanges(...ranges);
        const l = ranges[0];
        const w = ranges[1];
        const r = ranges[2];
        switch (combi) {
            case 7:
                // tslint:disable-next-line:no-console
                console.warn('Viewport: -right- will be ignored ' + ranges);
            case 6:
                return [l, w, s - l - w];
            case 5:
                return [l, s - l - r, r];
            case 4:
                return [l, s - l, 0];
            case 3:
                return [s - w - r, w, r];
            case 2:
                const lr = (s - w) / 2;
                return [lr, w, lr];
            case 1:
                return [0, s - r, r];
            case 0:
            default:
                return [0, s, 0];
        }
    }
}
//# sourceMappingURL=Viewport.js.map